/* eslint-disable */
import React, { useMemo, useContext, useState, useRef, useEffect } from 'react';
import { compose, contains } from 'underscore';
import classNames from 'classnames';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { mergeI18n } from '@bingads-webui-campaign-react/i18n-merger';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { buildLegacyI18n } from '@bingads-webui/build-legacy-i18n';
import { FilterService } from '@bingads-webui/filter-service';
import { ToolbarContainer } from '@bingads-webui-campaign-react/toolbar-container';
import { useConstructor } from '@bingads-webui-campaign/hooks';
import { I18nContext, withI18nConsumer } from '@bingads-webui-react/i18n-context';
import { assertIsDefined, assertNever } from '@bingads-webui-campaign-react/debug-assert';
import { legacyKeys } from '@bingads-webui-campaign-react/hotel-campaign-shared';
import { getToolbarEditor } from '@bingads-webui-campaign-react/toolbar-plugin';
import { CampaignShimmeredDetailsList } from '@bingads-webui-grid/campaign-details-list';
import { useColumnsWithToolbarColumnChooser } from '@bingads-webui-campaign/hotel-column-chooser';
import { IColumn, CheckboxVisibility } from '@fluentui/react';
import { useForceUpdate } from '@fluentui/react-hooks';
import { buildTreeViewItems, I18n } from '@bingads-webui-grid/well-known';
import { SelectionMode } from '@bingads-webui-react/fabric-control';
import { useDateRange } from '@bingads-webui-campaign-react/wizard-shared-components';
import { DisclaimerBanner } from '@bingads-webui-campaign-react/help';

import { PanelSelector } from './treeViewPanelSelector/PanelSelector';
import { getColumnChooserCategories } from './render/toolbar/ColumnChooser';
import { convertFilterEntity2$filter } from './legacy-adapter/filter-entity';
import { legacyI18nOfListingGroup } from './legacyI18nMap';
import { Model$ListingGroup } from './ApiType';
import { OdataSystemQueryOptions } from './API/odata';
import { useAPIsOfListingGroup } from './API';
import { styles } from './style';
import { getGridToolbarConfig } from './toolbarConfig';
import { GridType, GRID_NAME, ScopeLevel, nameColumnOdataPath, LISTING_GROUP_PERF_MARKER,
  LISTING_GROUP_LIST_ROUTING, LISTING_GROUP_TREE_ROUTING, treeViewButtonId, listViewButtonId } from './constant';
  import { ListingGroupGridItemData } from './interface';
import { getFilterConfig } from './filter-config';
import { columnKeyRawDataMap, odataMapOfListingGroupResponse, cpsColumnKeys } from './ColumnOdataMap';
import { AssetGroupNameColumn, ListingGroupColumn, IncludeExcludeColumn } from './rawColumns';
import { getColumnRenderByColumnKey } from './render';
import { isSortEnabledByColumnKey } from './render/sortByHelper';
import { useLogger } from './logger';
import { useTreeViewPanelSelector } from './useTreeViewPanelSelector';
import { isEveryThingElseRow, } from './utils';
import { getPropertyByPath } from './render/utils';

/**
 * default value used in fluent-ui
 * reference https://github.com/microsoft/fluentui/blob/31345de30d356731eeb0ac1e658a2652f8f00aa7/packages/react/src/components/DetailsList/DetailsList.base.tsx
 */
const MIN_COLUMN_WIDTH = 120;
const FIRST_COLUMN_WIDTH = 40;

interface ListingGroupsPageProps {
  preferenceService: any;
  permissions: any;
  gridType: GridType;
  scope: { save: Function, values: any };
  perfMarker: any;
  currentCampaign: any;
}

interface BuildItemDataOptions {
  rawData: Model$ListingGroup[] | undefined;
  gridType: GridType;
  serverContext: any;
  scope: any;
  i18n: I18n;
  expandedItemIds: Set<number>;
}

interface RefreshGridDataOptions {
  gridType?: GridType;
  $top: OdataSystemQueryOptions['$top'];
  $skip: OdataSystemQueryOptions['$skip'];
  $orderby?: OdataSystemQueryOptions['$orderby'];
  $select?: OdataSystemQueryOptions['$select'];
  $filter?: OdataSystemQueryOptions['$filter'];
}

type SortOrder = 'asc' | 'desc';

enum ToolbarShownItem {
  Toolbar,
  Download,
  ColumnChooser,
  FilterView,
  BidEditor,
}

enum InitializationState {
  NotStart,
  Running,
  Done,
}
const defaultPageIndex = 1;
const defaultPageSize = 20;
const maxPageSize = 20000;
const metaColumnOdataPaths: string[] = [
  'AssetGroupId',
  'AssetGroupName',
  'Condition',
  'Id',
  'IsLeaf',
  'ParentConditions',
  'ParentId',
];

function buildColumns(gridType: GridType, selectedColumnKeys: string[], sortOrder: SortOrder, i18n: I18n, permissions: any): IColumn[] {
  const bindSortFunction = permissions?.dynamic?.ListingGroupsAllColumnsSortSupport ? bindSortSupportAll : bindSort;

  const result = selectedColumnKeys
    .filter(filterByPermissions)
    .map(buildColumnBase)
    .map(bindOnRender)
    .map(bindSortFunction);

  return result;

  function filterByPermissions(columnKey: string): boolean {
    const cpsColumnsEnabled = permissions && permissions.dynamic && permissions.dynamic.SupportCPSForPmax;

    if (!cpsColumnsEnabled) {
      return !contains(cpsColumnKeys, columnKey);
    }
    return true
  }

  function buildColumnBase(columnKey: string, index: number): IColumn {
    const columnRawData = columnKeyRawDataMap[columnKey];
    assertIsDefined(columnRawData, 'Column is not defined in listing groups column raw data map');
    const column = {
      key: columnKey,
      className: columnKey,
      minWidth: index !== 0 ? MIN_COLUMN_WIDTH : FIRST_COLUMN_WIDTH,
      maxWidth: index !== 0 ? 300 : FIRST_COLUMN_WIDTH,
      isResizable: index !== 0,
      name: i18n.getString(columnRawData.name),
      fieldName: columnKey,
      onRenderHeader: index !== 0 ? undefined : () => (<div/>),
    };
    return column;
  }

  function bindOnRender(column: IColumn, index: number): IColumn {
    column.onRender = getColumnRenderByColumnKey(column.key);
    return column;
  }

  function bindSort(column: IColumn): IColumn {
    const enhancedColumn = {
      ...column,
      sortable: isSortEnabledByColumnKey(column.key),
      isSortedDescending: sortOrder === 'desc',
    };
    return enhancedColumn;
  }

  function bindSortSupportAll(column: IColumn): IColumn {
    const enhancedColumn = {
      ...column,
      sortable: true,
      isSortedDescending: sortOrder === 'desc',
    };
    return enhancedColumn;
  }
}

const ListingGroupsPage = (props: ListingGroupsPageProps) => {
  const {
    preferenceService, permissions, gridType, scope, perfMarker, currentCampaign
  } = props;

  const [listingGroupPerfMarker] = useConstructor(() => {
    const marker = perfMarker.createChild(LISTING_GROUP_PERF_MARKER);
    marker.willInit();
    return marker;
  });
  const defaultColumns = [IncludeExcludeColumn.key, ListingGroupColumn.key, AssetGroupNameColumn.key];

  const [allRowRawData, setAllRowRawData] = useState<Model$ListingGroup[] | undefined>(undefined);
  const serverContext: any = useContext(ServerContext);

  const logger = useLogger();
  const i18nWithTL = (useContext(I18nContext) as I18n);
  const i18n = useMemo(() => mergeI18n(
    buildLegacyI18n(i18nWithTL, [...legacyKeys, legacyI18nOfListingGroup]),
    i18nWithTL
  ), [i18nWithTL]);

  const scopeLevel = (() => {
    if (!scope.values.assetGroupId) {
      return ScopeLevel.Campaign;
    }
    return ScopeLevel.AssetGroup;
  })();

  const { toolbarSelectedConfig, toolbarUnselectedConfig } = useMemo(() => getGridToolbarConfig({
    scopeLevel,
    i18n,
    onBidChangeClick: () => {},
    permissions: {},
    selectedItems: [],
    serverContext: {},
  }), [i18n, scopeLevel]);
  const [toolbarShownItem, setToolbarShownItem] = useState(ToolbarShownItem.Toolbar);
  const latestGridDataRequestId = useRef(0);
  const styleClassNames: any = useDefaultStyles(styles);

  const toolbarEditor = getToolbarEditor({
    toolbarConfig: {
      toolbarSelectedConfig,
      toolbarUnselectedConfig,
    },
    updateToolbar: undefined,
    overrideWith: undefined,
  });

  const filterConfig = useMemo(() => getFilterConfig({ preferenceService, i18n }), [preferenceService, i18n]);

  const filterService = useMemo(() => {
    const filterService = new FilterService(filterConfig);
    return filterService;
  }, [filterConfig]);
  const [pageIndex, setPageIndex] = useState(defaultPageIndex);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [sortColumnOdataPath, setSortColumnOdataPath] = useState(nameColumnOdataPath);
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');

  const [pageItemsTotal, setPageItemsTotal] = useState(0);
  const availablePageSizes = [20, 50, 100, 200];

  const {
    expandedItemIds,
    setExpandedItemIds,
    isTreeViewSelectorOpen,
    treeViewSelectorColumns,
    treeViewSelectorDimensions,
    treeViewSelectorInitialSelectedItems,
    treeViewSelectorItems,
    treeViewSelectorHeaderText,
    onTreeViewCancel,
    onTreeViewSave,
    onTreeViewSelectedDimensionChange,
    isTreeViewShimmerEnabled,
    getErrorsForTreeViewSelector,
    onTreeViewColumnAddOrEdit,
  } = useTreeViewPanelSelector({
    i18n, allRowRawData, setAllRowRawData, scope, currentCampaign,
    refreshGridData,getArguemntsOfUpdateGridDataByCurrentState
  });

  const pager = {
    pageNumber: pageIndex,
    total: pageItemsTotal,
    pageSize,
    availablePageSizes,
    onPageChange,
    onPageSizeChange,
  };

  function onPageChange(newPageIndex) {
    logger.signal('Page index is changed');
    setPageIndex(newPageIndex);
    refreshGridData({
      ...getArguemntsOfUpdateGridDataByCurrentState(),
      ...getOdataSkipAndTop(newPageIndex, pageSize),
    });
  }

  function onPageSizeChange(newPageSize) {
    logger.signal('Page size is changed');
    setPageSize(newPageSize);
    refreshGridData({
      ...getArguemntsOfUpdateGridDataByCurrentState(),
      ...getOdataSkipAndTop(pageIndex, newPageSize),
    });
  }

  function changeToListView() {
    scope.save(LISTING_GROUP_LIST_ROUTING);
    // setCurrentGridType(GridType.List);
    changeActiveViewButton(GridType.List);
    logger.signal('change to list view');
  }

  function changeToTreeView() {
    scope.save(LISTING_GROUP_TREE_ROUTING);
    // setCurrentGridType(GridType.Tree);
    changeActiveViewButton(GridType.Tree);
    logger.signal('change to tree view');
  }

  function changeActiveViewButton(gridType: GridType) {
    toolbarEditor.update({
      id: treeViewButtonId,
      classes: ['view-switch-button', gridType === GridType.Tree ? 'btn-primary' : '', gridType === GridType.Tree ? 'active' : 'inactive'],
      ariaLabel: gridType === GridType.Tree ? i18n.getString(_TL_('Switch to hierarchy view, selected')) : i18n.getString(_TL_('Switch to hierarchy view, unselected')),
    });
    toolbarEditor.update({
      id: listViewButtonId,
      classes: ['view-switch-button', gridType === GridType.List ? 'btn-primary' : '', gridType === GridType.List ? 'active' : 'inactive'],
      ariaLabel: gridType === GridType.List ? i18n.getString(_TL_('Switch to list view, selected')) : i18n.getString(_TL_('Switch to list view, unselected')),
    });
  }

  function buildItemData({
    gridType, rawData, i18n, serverContext, expandedItemIds, scope,
  }: BuildItemDataOptions): ListingGroupGridItemData[] | undefined {
    if (rawData === undefined) {
      return undefined;
    }
    const items = rawData.map(raw => ({
      ...raw,
      serverContext,
      i18n,
      scope,
      eventHandlers: {},
      refreshGridData: () => {
        logger.signal('Refresh grid from item');
        refreshGridData(getArguemntsOfUpdateGridDataByCurrentState())
      }
    }));
    // registerEventHandlersForInlineEidtor(items);
    if (gridType === GridType.Tree) {
      const listingGroupGridItemsData = buildTreeViewItems({
        expandedItemIds,
        getParentKeyOfItem: item => item.ParentId,
        getPrimaryKeyOfItem: item => item.Id,
        items,
      });
      registerEventHandlersForTreeView(listingGroupGridItemsData);
      return listingGroupGridItemsData;
    }
    return items;
  }

  function registerEventHandlersForTreeView(items: ListingGroupGridItemData[]) {
    items.forEach(item => {
      item.eventHandlers.onTreeViewColumnAddOrEdit = onTreeViewColumnAddOrEdit;

      item.eventHandlers.onTreeViewColumnToggleCollapse = (item) => {
        assertIsDefined(item?.treeViewInfo);
        const id = item.Id;
        if (item.treeViewInfo.isExpanded)
          expandedItemIds.delete(id);
        else
          expandedItemIds.add(id);
        setExpandedItemIds(new Set(expandedItemIds));
      }
    });
  }

  function getSortedColumn() {
    const sortColumn = columns.find(c =>
      odataMapOfListingGroupResponse.get(c.key) === sortColumnOdataPath);
    assertIsDefined(sortColumn);
    return sortColumn;
  }

  const { listingGroupGridData, createRootListingTree } = useAPIsOfListingGroup();
  const {
    isOrderByEnabled,
    isSelectEnabled,
    isFilterEnabled,
  } = scope.values;
  const commonEntityPath = {
    accountId: scope.values.aid,
    campaignId: scope.values.campaignId,
    customerId: scope.values.cid,
    assetGroupId: scope.values.assetGroupId
  };
  const dataRange = useDateRange();
  const dataRangeCacheKey = `${dataRange.StartDate}-${dataRange.EndDate}`;

  function onColumnChooserApply(newColumnKeys: string[]) {
    // change to loading state
    // columns might update faster than items, in this case it will try to get value that is not existed yet.
    setAllRowRawData(undefined);
    setToolbarShownItem(ToolbarShownItem.Toolbar);
    // logger.signal('choose new columns');
  }

  function onColumnChooserCancel() {
    setToolbarShownItem(ToolbarShownItem.Toolbar);
    // logger.signal('cancel column chooser');
  }
  function getOdataOrderby(sortColumnOdataPath: string, sortOrder: SortOrder) {
    return {
      $orderby: isOrderByEnabled !== 'false' ? `${sortColumnOdataPath} ${sortOrder}` : undefined,
    };
  }
  function getOdataSkipAndTop(pageIndex, pageSize) {
    return {
      $skip: (pageIndex - 1) * pageSize,
      $top: pageSize,
    };
  }
  function getOdataSelect(selectedColumnKeys: string[]) {
    if (isSelectEnabled === 'false') {
      return {
        $select: undefined,
      };
    }
    const selectedColumnOdataPaths = selectedColumnKeys.map((selectedColumnKey) => {
      const columnOdataPath = odataMapOfListingGroupResponse.get(selectedColumnKey);
      assertIsDefined(columnOdataPath);
      return columnOdataPath;
    });

    return {
      $select: selectedColumnOdataPaths.concat(metaColumnOdataPaths).join(','),
    };
  }

  function getOdataFilter() {
    return {
      $filter: isFilterEnabled !== 'false' ? $filterContainer.current : undefined,
    };
  }

  function getArguemntsOfUpdateGridDataByCurrentState() {
    return {
      ...getOdataSkipAndTop(pageIndex, pageSize),
      ...getOdataOrderby(sortColumnOdataPath, sortOrder),
      ...getOdataSelect(trickySelectedColumnKeys),
      ...getOdataFilter(),
      gridType: currentGridType,
    };
  }

  async function refreshGridData({
    $top,
    $skip,
    $filter,
    $orderby = undefined,
    $select = undefined,
    gridType = currentGridType,
  }: RefreshGridDataOptions) {
    if (initializationState.current !== InitializationState.Done) {
      return;
    }
    // change to loading state
    setAllRowRawData(undefined);
    latestGridDataRequestId.current += 1;
    const requestId = latestGridDataRequestId.current;

    listingGroupPerfMarker.willFetchData();

    logger.signal(`listing group grid data request #${requestId}`);

    const response = await listingGroupGridData({
      queryOptions: {
        $top: gridType === GridType.Tree ? maxPageSize : $top,
        $skip,
        $filter,
        $orderby,
        $select,
        $count: true,
      },
      entityPath: commonEntityPath,
      payload: {
        gridDataParameters: {
          DateRange: {
            StartDate: dataRange.StartDate,
            EndDate: dataRange.EndDate,
          },
        },
      },
    });
    const gridData = response.value;

    if (requestId === latestGridDataRequestId.current) {
      listingGroupPerfMarker.willRender();
      setAllRowRawData(gridData);
      setPageItemsTotal(response['@odata.count']);
    }

    if (document.getElementsByClassName('ms-DetailsList-headerWrapper')) {
      const gridIndex = document.getElementsByClassName('ms-DetailsList-headerWrapper').length;
      document.getElementsByClassName('ms-DetailsList-headerWrapper')[gridIndex - 1].parentElement.setAttribute('aria-busy', 'true');
    }
  }

  async function initial() {
    initialColumns();
  }

  function onSort(column: IColumn) {
    logger.signal('Sort column or sort order is changed');
    const newSortOrder = column.isSortedDescending ? 'desc' : 'asc';
    const newSortColumnOdataPath = odataMapOfListingGroupResponse.get(column.key);
    assertIsDefined(newSortColumnOdataPath);
    setSortColumnOdataPath(newSortColumnOdataPath);
    setSortOrder(newSortOrder);
    if (currentGridType === GridType.List) {
      refreshGridData({
        ...getArguemntsOfUpdateGridDataByCurrentState(),
        ...getOdataOrderby(newSortColumnOdataPath, newSortOrder),
      });
    }
  }

  const resetToolbarVisibility = ({ forceHideToolbar }: { forceHideToolbar: boolean }) => {
    if (forceHideToolbar) {
      setToolbarShownItem(ToolbarShownItem.FilterView);
    } else {
      setToolbarShownItem(ToolbarShownItem.Toolbar);
    }
  };
  const forceUpdate = useForceUpdate();
  const currentGridType = gridType;
  const initializationState = useRef(InitializationState.NotStart);
  const onFilterStateChanged = useRef<Function>(() => { });
  // Filter is special, generally, it should be a state. However, we are using legacy component, and it's not fully controlled and kind of subtle.
  const $filterContainer = useRef('');

  const [isRefreshReady, setIsRefreshReady] = useState(false);

  // reregister the callback to make sure there is no closure issue so that we could get latest state.
  // And we could not remove other callbacks. For example, the toolbar component might add callback too.
  filterService.off('filter-state-changed', onFilterStateChanged.current);
  onFilterStateChanged.current = async () => {
    const filterEntity = filterService.getEditableFilter();
    const filterString = convertFilterEntity2$filter(filterEntity);
    $filterContainer.current = filterString;
    let gridType = currentGridType;

    switch (true) {
      // as long as there is filter, jump to list view.
      // TODO: add a test
      case filterString && currentGridType === GridType.Tree:
        changeToListView();
        gridType = GridType.List;
      // no filter --> some filter, not allow change to tree
      // some filter --> no filter, allow change to tree
      // TODO: add a test
      case currentGridType === GridType.List:
        // toolbarChangeOnFilterChange(filterString);
        break;
      default:
        break;
    }

    // tricky, initial here for we only want to send one request to get grid data after everything is initialized.
    // but filter is iniitialized by itself.
    switch (initializationState.current) {
      case InitializationState.NotStart:
        initializationState.current = InitializationState.Running;
        await initial();
        initializationState.current = InitializationState.Done;
        setIsRefreshReady(true);
        break;
      case InitializationState.Running:
        break;
      case InitializationState.Done:
        logger.signal('Refresh grid by filter change');
        refreshGridData({ ...getArguemntsOfUpdateGridDataByCurrentState(), gridType });
        forceUpdate();
        break;
      default:
        assertNever(initializationState.current);
    }
  };

  filterService.on('filter-state-changed', onFilterStateChanged.current);

  const {
    initialColumns,
    columns: selectedColumnKeys,
    ColumnChooserTrivia,
    ColumnChooser,
  } = useColumnsWithToolbarColumnChooser({
    defaultColumns,
    gridName: GRID_NAME,
    toolbarOptions: {
      toolbarEditor,
      openColumnChooser: () => { setToolbarShownItem(ToolbarShownItem.ColumnChooser); },
    },
  });
  const toolbarEditorUpdate = useMemo(() => toolbarEditor.update.bind(toolbarEditor), [toolbarEditor]);

  const toolbar = (
    <React.Fragment>
      {/* campaignContentContainer is for inline-chart. Read code of inline-chart for details :)
      <div className='hotel-group-inline-chart campaignContentContainer'>
        {inlineChart}
      </div> */}
      {toolbarShownItem === ToolbarShownItem.ColumnChooser &&
        <ColumnChooser
          categories={getColumnChooserCategories({
            gridType: GridType.List,
            i18n,
            scopeLevel,
            permissions,
          })}
          legacyI18n={i18n}
          selected={selectedColumnKeys}
          onApply={onColumnChooserApply}
          onCancel={onColumnChooserCancel}
        />
      }
      <ToolbarContainer
        i18n={i18n}
        forceHideToolbar={toolbarShownItem !== ToolbarShownItem.Toolbar}
        showToolbarSelected={false}
        showToolbarUnselected
        toolbarUnselectedConfig={toolbarUnselectedConfig}
      />
    </React.Fragment>
  );

  let trickySelectedColumnKeys = selectedColumnKeys.filter(k => {
    return columnKeyRawDataMap[k]; // only when the key is existed in columnKeyRawDataMap, we keep it.
  });

  const listingGroupColumnIndex = trickySelectedColumnKeys.findIndex(k => k === ListingGroupColumn.key);
  trickySelectedColumnKeys = trickySelectedColumnKeys.filter(key => key !== AssetGroupNameColumn.key);
  if (scopeLevel === ScopeLevel.Campaign) {
    trickySelectedColumnKeys.splice(listingGroupColumnIndex + 1, 0, AssetGroupNameColumn.key);
  }

  const columns = buildColumns(GridType.List, trickySelectedColumnKeys, sortOrder, i18n, permissions);

  const filteredRowData = (() => {
    // when in grid type is tree, we sort the data in front-end
    if (currentGridType === GridType.Tree) {
      const sortedItems = allRowRawData?.slice()?.sort(compareFn);
      return sortOrder === "desc" ? sortedItems?.reverse() : sortedItems;
    }
    return allRowRawData;

    function compareFn(a: Model$ListingGroup, b: Model$ListingGroup) {
      if (sortColumnOdataPath === nameColumnOdataPath) {
        // direction: -1 for desc, 1 for asc
        if (isEveryThingElseRow(a)) {
          return 1;
        }

        if (isEveryThingElseRow(b)) {
          return -1;
        }
      }
      const valueA = getPropertyByPath(a, sortColumnOdataPath);

      const valueB = getPropertyByPath(b, sortColumnOdataPath);

      let v1 = valueA;
      let v2 = valueB;
      if (typeof valueA === 'string' && typeof valueB === 'string' && sortColumnOdataPath === nameColumnOdataPath) {
        v1 = valueA.toLowerCase();
        v2 = valueB.toLowerCase();
      }

      if (v1 < v2) {
        return -1;
      }
      if (v1 > v2) {
        return 1;
      }
      return 0;
    }
  })();

  const items = buildItemData({
    scope,
    serverContext,
    i18n,
    gridType: currentGridType,
    rawData: filteredRowData,
    expandedItemIds,
  });

  useEffect(() => {
    if (isRefreshReady) {
      logger.signal('Refresh grid by column/range/type change');
      refreshGridData({ ...getArguemntsOfUpdateGridDataByCurrentState() });
    }
  }, [selectedColumnKeys, dataRangeCacheKey, gridType, scope.values, isRefreshReady]);

  // setup toolbar
  useEffect(() => {
    toolbarEditor.update({
      id: treeViewButtonId,
      onClick: changeToTreeView,
    });
    toolbarEditor.update({
      id: listViewButtonId,
      onClick: changeToListView,
    });

    // initial for the first time mount
    changeActiveViewButton(currentGridType);
  }, [toolbarSelectedConfig, toolbarUnselectedConfig]);

  const [creatingRootNode, setCreatingRootNode] = useState(false);

  const createFirstListingGroupLink = useMemo(() => {
    if (scopeLevel !== ScopeLevel.AssetGroup) {
      // do not show create first listing group link when scope is campaign scope
      return null;
    }

    if (!items || items.length > 0) {
      // only show create first listing group link when there is no listing group
      return null;
    }

    const createRootNode = () => {
      setCreatingRootNode(true);
      createRootListingTree(scope.values.assetGroupId)
      .then(() => {
        setCreatingRootNode(false);
        refreshGridData(getArguemntsOfUpdateGridDataByCurrentState());
      }).catch(() => {
        setCreatingRootNode(false);
      });
    };

    return (
      <div className='create-first-listing-group'>
        <div>{i18n.getString(_TL_('You haven\'t created any listing groups for this asset group yet.'))}</div>
        <a onClick={createRootNode}>{i18n.getString(_TL_('Create first listing group.'))}</a>
      </div>
    )
  }, [items, scope.values.assetGroupId, scopeLevel]);

  return (
    <div
      className={classNames('listing-group-page', ' small-padding-left', styleClassNames['listing-group-content-root'])}
    >
      {isTreeViewSelectorOpen && <PanelSelector
        getErrors={getErrorsForTreeViewSelector}
        isOpen={isTreeViewSelectorOpen}
        columns={treeViewSelectorColumns}
        dimensions={treeViewSelectorDimensions}
        headerText={treeViewSelectorHeaderText}
        initialSelectedItems={treeViewSelectorInitialSelectedItems}
        items={treeViewSelectorItems}
        onCancel={onTreeViewCancel}
        onSave={onTreeViewSave}
        onSelectedDimensionChange={onTreeViewSelectedDimensionChange}
        enableShimmer={isTreeViewShimmerEnabled}
        keyOfItem={(item) => item.key}
        isBulkAddEnabledForDimension={(d) => d.data.isBulkAddEnabled}
      />}
      <CampaignShimmeredDetailsList
        enableShimmer={items === undefined || creatingRootNode}
        key={currentGridType.toString()}
        items={items ?? []}
        toolbar={toolbar}
        columns={columns}
        onSort={onSort}
        sortedColumn={getSortedColumn()}
        checkboxVisibility={CheckboxVisibility.hidden}
        selectionMode={SelectionMode.multiple}
        freezingColumnCount={0}
        pager={currentGridType === GridType.List ? pager : undefined}
        didUpdate={(prev) => {
          if (prev.enableShimmer && items) {
            listingGroupPerfMarker.done();
          }
        }}
        onRenderRow={(detailsRowProps, defaultRender) => {
          if (detailsRowProps.focusZoneProps) {
            detailsRowProps.focusZoneProps.preventFocusRestoration = true;
          } else {
            detailsRowProps.focusZoneProps = { preventFocusRestoration: true };
          }
          return defaultRender(detailsRowProps);
        }}
      />
      { createFirstListingGroupLink }
      <ColumnChooserTrivia />
      <DisclaimerBanner timezone={serverContext.CurrentAccount.TimeZone} />
    </div>
  );
};

export const StyledListingGroupsPage = compose(
  withI18nConsumer,
  WrappedComponent => withDefaultStyles(WrappedComponent, () => ({}))
)(ListingGroupsPage);